/**
 * Per-league in-memory page cache helpers (Home / Picks / Standings).
 * Live AppSync events should update every cached entry that contains the game.
 *
 * Session stores survive SPA route changes (page unmount); cleared on full reload.
 */

import {
  collectGamesFromRoundsData,
  mergeLiveGameIntoGamesList,
  mergeLiveGameIntoRoundsData
} from './mergeLiveGame';

function createLeaguePageStore() {
  let store = Object.create(null);
  return {
    get: () => store,
    set: (next) => {
      store = next && typeof next === 'object' ? next : Object.create(null);
      return store;
    },
    reset: () => {
      store = Object.create(null);
    }
  };
}

/** @type {{ get: () => Record<string, unknown>, set: (next: Record<string, unknown>) => Record<string, unknown>, reset: () => void }} */
export const homeByLeagueStore = createLeaguePageStore();
/** @type {{ get: () => Record<string, unknown>, set: (next: Record<string, unknown>) => Record<string, unknown>, reset: () => void }} */
export const betsByLeagueStore = createLeaguePageStore();
/**
 * Dirty-baseline snapshots for Picks SAVE (`pk:…` / `league:roundKey` → serialized bets).
 * Must survive SPA remounts alongside betsByLeagueStore — otherwise cache restore leaves SAVE stuck disabled.
 * @type {{ get: () => Record<string, unknown>, set: (next: Record<string, unknown>) => Record<string, unknown>, reset: () => void }}
 */
export const savedSnapshotsStore = createLeaguePageStore();
/** @type {{ get: () => Record<string, unknown>, set: (next: Record<string, unknown>) => Record<string, unknown>, reset: () => void }} */
export const standingsByLeagueStore = createLeaguePageStore();

/**
 * Last selected competition code for LeagueCircleSelector (Home / Picks / Standings).
 * Survives SPA navigations; cleared with resetLeaguePageCaches / full reload.
 * @type {{ get: () => string | null, set: (code: string | null | undefined) => string | null, reset: () => void }}
 */
let selectedLeagueCode = null;
export const selectedLeagueCodeStore = {
  get: () => selectedLeagueCode,
  set: (code) => {
    selectedLeagueCode = typeof code === 'string' && code ? code : null;
    return selectedLeagueCode;
  },
  reset: () => {
    selectedLeagueCode = null;
  }
};

/** @typedef {'previous' | 'current' | 'next'} RoundTypeSelection */
const VALID_ROUND_TYPES = new Set(['previous', 'current', 'next']);

/**
 * Last selected round pill (previous / current / next) for Picks ↔ Standings.
 * Survives SPA navigations; cleared with resetLeaguePageCaches / full reload.
 * @type {{ get: () => RoundTypeSelection | null, set: (type: string | null | undefined) => RoundTypeSelection | null, reset: () => void }}
 */
let selectedRoundType = null;
export const selectedRoundTypeStore = {
  get: () => selectedRoundType,
  set: (type) => {
    selectedRoundType = VALID_ROUND_TYPES.has(type) ? type : null;
    return selectedRoundType;
  },
  reset: () => {
    selectedRoundType = null;
  }
};

/**
 * Pick the league circle selection, preferring the in-page value, then the session store.
 * @param {Array<{ code: string }>} preferredLeagues
 * @param {{ code: string } | null | undefined} current
 * @returns {{ code: string } | null}
 */
export function resolveSessionSelectedLeague(preferredLeagues, current) {
  if (!preferredLeagues?.length) return null;
  if (current && preferredLeagues.some((league) => league.code === current.code)) {
    return current;
  }
  const storedCode = selectedLeagueCodeStore.get();
  const fromStore = storedCode
    ? preferredLeagues.find((league) => league.code === storedCode)
    : null;
  const next = fromStore || preferredLeagues[0];
  if (next?.code && next.code !== storedCode) {
    selectedLeagueCodeStore.set(next.code);
  }
  return next;
}

/**
 * Prefer the session-stored round pill when that round exists on this page; else fallback.
 * Writes the resolved value back to the store so Picks and Standings stay aligned.
 * @param {(type: RoundTypeSelection) => boolean} isAvailable
 * @param {RoundTypeSelection} fallback
 * @returns {RoundTypeSelection}
 */
export function resolveSessionSelectedRoundType(isAvailable, fallback) {
  const stored = selectedRoundTypeStore.get();
  if (stored && typeof isAvailable === 'function' && isAvailable(stored)) {
    return stored;
  }
  const next = VALID_ROUND_TYPES.has(fallback) ? fallback : 'current';
  if (next !== stored) {
    selectedRoundTypeStore.set(next);
  }
  return next;
}

/** Clear all session page caches (logout / session expiry / tests). */
export function resetLeaguePageCaches() {
  homeByLeagueStore.reset();
  betsByLeagueStore.reset();
  savedSnapshotsStore.reset();
  standingsByLeagueStore.reset();
  selectedLeagueCodeStore.reset();
  selectedRoundTypeStore.reset();
}

/**
 * @param {Record<string, unknown>} cache — leagueCode → roundsData
 * @param {string} gameId
 * @param {Record<string, unknown>} snapshot
 * @returns {Record<string, unknown>}
 */
export function applyLiveEventToRoundsCache(cache, gameId, snapshot) {
  if (!cache || !gameId || !snapshot) return cache || {};
  let changed = false;
  const next = { ...cache };
  for (const code of Object.keys(cache)) {
    const roundsData = cache[code];
    if (!roundsData) continue;
    const merged = mergeLiveGameIntoRoundsData(roundsData, gameId, snapshot);
    if (merged !== roundsData) {
      next[code] = merged;
      changed = true;
    }
  }
  return changed ? next : cache;
}

/**
 * @param {Record<string, { currentWeek?: unknown, games?: unknown[] }>} cache
 * @param {string} gameId
 * @param {Record<string, unknown>} snapshot
 */
export function applyLiveEventToHomeCache(cache, gameId, snapshot) {
  if (!cache || !gameId || !snapshot) return cache || {};
  let changed = false;
  const next = { ...cache };
  for (const code of Object.keys(cache)) {
    const entry = cache[code];
    if (!entry?.games) continue;
    const games = mergeLiveGameIntoGamesList(entry.games, gameId, snapshot);
    if (games !== entry.games) {
      next[code] = {
        ...entry,
        games,
        currentWeek: entry.currentWeek
          ? { ...entry.currentWeek, games }
          : entry.currentWeek
      };
      changed = true;
    }
  }
  return changed ? next : cache;
}

/**
 * Flatten games from all cached betting roundsData (+ optional current).
 * @param {Record<string, unknown>} roundsCache
 * @param {Record<string, unknown> | null | undefined} currentRoundsData
 */
export function collectGamesFromRoundsCache(roundsCache, currentRoundsData) {
  const byId = new Map();
  const add = (games) => {
    for (const g of games) {
      if (g?.gameId == null) continue;
      byId.set(String(g.gameId), g);
    }
  };
  for (const code of Object.keys(roundsCache || {})) {
    add(collectGamesFromRoundsData(roundsCache[code]));
  }
  // Prefer the active view's copy when present
  if (currentRoundsData) add(collectGamesFromRoundsData(currentRoundsData));
  return [...byId.values()];
}

/**
 * Flatten games from all home league cache entries (+ optional current list).
 * @param {Record<string, { games?: unknown[] }>} homeCache
 * @param {unknown[] | null | undefined} currentGames
 */
export function collectGamesFromHomeCache(homeCache, currentGames) {
  const byId = new Map();
  const add = (games) => {
    if (!Array.isArray(games)) return;
    for (const g of games) {
      if (g?.gameId == null) continue;
      byId.set(String(g.gameId), g);
    }
  };
  for (const code of Object.keys(homeCache || {})) {
    add(homeCache[code]?.games);
  }
  add(currentGames);
  return [...byId.values()];
}
